import {useMemo} from 'react';

import type {FrontendTmdb} from '@repo/shared/api/website_api';
import type {NonEmptyArray} from '@repo/shared/lib/type_utils';
import {neverHappens} from '@repo/shared/lib/type_utils';

import {
  useNumberQueryString,
  useStringArrayQueryString,
  useStringQueryString,
} from '@repo/shared-web/lib/use_query_string';

import {TmdbSort} from '@repo/website-frontend/components/search_bar';
import {MIN_VOTE_COUNT} from '@repo/website-frontend/lib/constants';
import {useAllTmdb} from '@repo/website-frontend/stores/tmdb_store';

const SIX_MONTHS = 6 * 30 * 24 * 3600 * 1000;

export enum GenreFilter {
  Include = 'include',
  Exclude = 'exclude',
}

export function serializeGenre(genre: {name: string; mode: GenreFilter}): string {
  return `${genre.name}:${genre.mode}`;
}

export function parseGenre(genre: string): {name: string; mode: GenreFilter} {
  const [name = '', mode = ''] = genre.split(':');
  return {name, mode: mode as GenreFilter};
}

const WEEKS = 7 * 24 * 3600 * 1000;
const MONTHS = 30.5 * 24 * 3600 * 1000;
const YEARS = 365 * 24 * 3600 * 1000;

export const MAX_AGE_STEPS: NonEmptyArray<{value: number; label: string; hasButton?: true}> = [
  {value: Number.POSITIVE_INFINITY, label: 'All', hasButton: true},
  {value: 10 * YEARS, label: '10y', hasButton: true},
  {value: 8 * YEARS, label: '8y'},
  {value: 5 * YEARS, label: '5y'},
  {value: 4 * YEARS, label: '4y'},
  {value: 3 * YEARS, label: '3y'},
  {value: 2 * YEARS, label: '2y', hasButton: true},
  {value: YEARS, label: '1y'},
  {value: 10 * MONTHS, label: '10m'},
  {value: 8 * MONTHS, label: '8m'},
  {value: 6 * MONTHS, label: '6m', hasButton: true},
  {value: 5 * MONTHS, label: '5m'},
  {value: 4 * MONTHS, label: '4m'},
  {value: 3 * MONTHS, label: '3m'},
  {value: MONTHS, label: '1m', hasButton: true},
  {value: 3 * WEEKS, label: '3w'},
  {value: 2 * WEEKS, label: '2w'},
  {value: WEEKS, label: '1w', hasButton: true},
];
export const MAX_AGE_STEPS_VISIBLE = MAX_AGE_STEPS.filter(step => step.hasButton);

export function useTmdbSearchBar() {
  const [urlGenres, setUrlGenres] = useStringArrayQueryString('genres', []);
  const genres = useMemo(() => urlGenres.map(parseGenre), [urlGenres]);

  const [minScore, setMinScore] = useNumberQueryString('minScore', 0);
  const [maxAge, setMaxAge] = useStringQueryString('maxAge', '');
  const [sort, setSort] = useStringQueryString<TmdbSort>('sort', TmdbSort.Recents);
  const [title, setTitle] = useStringQueryString('title', '');

  return {
    genres,
    minScore,
    maxAge,
    sort,
    title,
    setUrlGenres,
    setMinScore,
    setMaxAge,
    setSort,
    setTitle,
  };
}

export const useFilteredItems = (): FrontendTmdb[] => {
  const allTmdb = useAllTmdb();
  const {title, genres, minScore, maxAge, sort} = useTmdbSearchBar();

  const items = useMemo(() => [...allTmdb.values()], [allTmdb]);
  const filteredItems = useMemo(() => {
    const filtered = items
      .filter(item => {
        if (title.length === 0) {
          return true;
        }
        return item.title.toUpperCase().includes(title.toUpperCase());
      })
      .filter(item => {
        if (minScore === 0) {
          return true;
        }
        if (item.voteAverage === undefined || (item.voteCount ?? 0) < MIN_VOTE_COUNT) {
          return false;
        }
        return 10 * item.voteAverage >= minScore;
      })
      .filter(item => {
        const itemAgeMs = Date.now() - (item.releaseDate ?? 0);
        const maxAgeTs =
          MAX_AGE_STEPS.find(step => step.label === maxAge)?.value ?? Number.POSITIVE_INFINITY;
        return itemAgeMs <= maxAgeTs;
      })
      .filter(item => {
        if (genres.length === 0) {
          return true;
        }
        const itemGenres = new Set(item.genres.map(g => g.name));
        const includeGenres = genres.filter(g => g.mode === GenreFilter.Include);
        const excludeGenres = genres.filter(g => g.mode === GenreFilter.Exclude);

        const includePassed =
          includeGenres.length === 0 ||
          includeGenres.some(g => itemGenres.has(g.name)) !== undefined;
        const excludePassed =
          excludeGenres.length === 0 ||
          excludeGenres.some(g => itemGenres.has(g.name)) === undefined;

        return includePassed && excludePassed;
      });

    if (sort === TmdbSort.Recents) {
      const sixMonthOld = filtered.filter(
        m => m.releaseDate !== undefined && Date.now() - m.releaseDate <= SIX_MONTHS,
      );
      const notSixMonthOld = filtered.filter(
        m => m.releaseDate === undefined || Date.now() - m.releaseDate > SIX_MONTHS,
      );
      return [
        ...sixMonthOld.sort((m1, m2) => m2.firstPubTs - m1.firstPubTs),
        ...notSixMonthOld.sort((m1, m2) => (m2.releaseDate ?? 0) - (m1.releaseDate ?? 0)),
      ];
    }
    if (sort === TmdbSort.ReleaseDate) {
      return filtered.sort((m1, m2) => {
        return (m2.releaseDate ?? 0) - (m1.releaseDate ?? 0);
      });
    }
    if (sort === TmdbSort.Score) {
      return filtered.sort((m1, m2) => {
        const v1 = (m1.voteCount ?? 0) >= MIN_VOTE_COUNT ? (m1.voteAverage ?? 0) : 0;
        const v2 = (m2.voteCount ?? 0) >= MIN_VOTE_COUNT ? (m2.voteAverage ?? 0) : 0;
        return v2 - v1;
      });
    }
    if (sort === TmdbSort.Popularity) {
      return filtered.sort((m1, m2) => {
        return (m2.popularity ?? 0) - (m1.popularity ?? 0);
      });
    }
    if (sort === TmdbSort.Budget) {
      return filtered.sort((m1, m2) => {
        return (m2.budget ?? 0) - (m1.budget ?? 0);
      });
    }
    if (sort === TmdbSort.BoxOffice) {
      return filtered.sort((m1, m2) => {
        return (m2.revenue ?? 0) - (m1.revenue ?? 0);
      });
    }

    neverHappens(sort);
  }, [items, sort, title, minScore, maxAge, genres]);

  return filteredItems;
};
